import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Barcode,
  CheckCircle2,
  Clock,
  Edit3,
  Lightbulb,
  PlusCircle,
  RefreshCw,
  Search,
  Sparkles,
  User,
  XCircle,
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/Button';
import AuthMessage from '../../components/AuthMessage';
import Drawer from '../../components/Drawer';
import { useAuth } from '../../context/AuthContext';
import { listProductSuggestions, updateProductSuggestion } from '../../api/suggestions';
import form from '../../styles/form.module.css';
import styles from './ProductSuggestions.module.css';

const STATUSES = [
  { id: 'ALL', label: 'All Requests' },
  { id: 'NEW', label: 'New / Unreviewed', icon: Sparkles },
  { id: 'ACKNOWLEDGED', label: 'Acknowledged', icon: Clock },
  { id: 'ADDED', label: 'Added to Catalog', icon: CheckCircle2 },
  { id: 'REJECTED', label: 'Rejected', icon: XCircle },
];

function formatDate(isoStr) {
  if (!isoStr) return '';
  try {
    const d = new Date(isoStr);
    return d.toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return isoStr;
  }
}

export default function ProductSuggestions() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');

  // Update Drawer State
  const [activeSuggestion, setActiveSuggestion] = useState(null);
  const [modalStatus, setModalStatus] = useState('ACKNOWLEDGED');
  const [opsNote, setOpsNote] = useState('');
  const [updating, setUpdating] = useState(false);
  const [modalError, setModalError] = useState('');

  const fetchSuggestions = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await listProductSuggestions(user.idToken);
      setSuggestions(data);
    } catch (err) {
      setError(err.message || 'Failed to fetch product suggestions.');
    } finally {
      setLoading(false);
    }
  }, [user.idToken]);

  useEffect(() => {
    if (user?.idToken) {
      fetchSuggestions();
    }
  }, [user?.idToken, fetchSuggestions]);

  const counts = useMemo(() => {
    const c = { ALL: suggestions.length, NEW: 0, ACKNOWLEDGED: 0, ADDED: 0, REJECTED: 0 };
    suggestions.forEach((s) => {
      if (c[s.status] !== undefined) c[s.status] += 1;
    });
    return c;
  }, [suggestions]);

  const filteredSuggestions = useMemo(() => {
    let list = suggestions;
    if (activeTab !== 'ALL') {
      list = list.filter((s) => s.status === activeTab);
    }
    const q = searchQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter((s) => (
      s.name.toLowerCase().includes(q)
      || s.barcode.toLowerCase().includes(q)
      || s.notes.toLowerCase().includes(q)
      || s.customerSub.toLowerCase().includes(q)
      || s.opsNote.toLowerCase().includes(q)
    ));
  }, [suggestions, activeTab, searchQuery]);

  const openUpdateDrawer = (item) => {
    setActiveSuggestion(item);
    setModalStatus(item.status);
    setOpsNote(item.opsNote || '');
    setModalError('');
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!activeSuggestion) return;
    setUpdating(true);
    setModalError('');
    try {
      const updated = await updateProductSuggestion(activeSuggestion.id, {
        status: modalStatus,
        opsNote,
      }, user.idToken);

      setSuggestions((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
      setActiveSuggestion(null);
    } catch (err) {
      setModalError(err.message || 'Could not update suggestion.');
    } finally {
      setUpdating(false);
    }
  };

  const handleOnboardClick = (suggestion) => {
    navigate('/products/bulk', {
      state: {
        prefill: {
          name: suggestion.name,
          barcode: suggestion.barcode,
        },
      },
    });
  };

  return (
    <div>
      <PageHeader
        eyebrow="Operations"
        title="Product Suggestions"
        subtitle="Customer requests for new products to stock in the store."
        actions={(
          <Button
            variant="outline"
            icon={RefreshCw}
            onClick={fetchSuggestions}
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        )}
      />

      <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <div className={`${styles.statIcon} ${styles.statIconTotal}`}>
            <Lightbulb size={20} />
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statValue}>{counts.ALL}</span>
            <span className={styles.statLabel}>Total Requests</span>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={`${styles.statIcon} ${styles.statIconNew}`}>
            <Sparkles size={20} />
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statValue}>{counts.NEW}</span>
            <span className={styles.statLabel}>New / Unreviewed</span>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={`${styles.statIcon} ${styles.statIconAck}`}>
            <Clock size={20} />
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statValue}>{counts.ACKNOWLEDGED}</span>
            <span className={styles.statLabel}>Acknowledged</span>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={`${styles.statIcon} ${styles.statIconAdded}`}>
            <CheckCircle2 size={20} />
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statValue}>{counts.ADDED}</span>
            <span className={styles.statLabel}>Added to Catalog</span>
          </div>
        </div>
      </div>

      <AuthMessage>{error}</AuthMessage>

      <div className={styles.controlsBar}>
        <div className={styles.tabs}>
          {STATUSES.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`${styles.tab} ${activeTab === t.id ? styles.tabActive : ''}`}
              onClick={() => setActiveTab(t.id)}
            >
              {t.label}
              <span className={styles.tabBadge}>{counts[t.id] ?? 0}</span>
            </button>
          ))}
        </div>

        <div className={styles.searchWrap}>
          <Search size={15} className={styles.searchIcon} />
          <input
            className={styles.searchInput}
            placeholder="Search suggestions by name, barcode, notes…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <div className={styles.tableScroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Suggested Product</th>
              <th>Customer Notes</th>
              <th>Status</th>
              <th>Ops Response</th>
              <th>Submitted Date</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {filteredSuggestions.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <div className={styles.emptyState}>
                    <Lightbulb size={32} className={styles.emptyIcon} />
                    <p className={styles.emptyTitle}>No suggestions found</p>
                    <p className={styles.emptyText}>
                      {searchQuery
                        ? 'Try adjusting your search query or status filter.'
                        : 'No customer product requests match this filter status.'}
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              filteredSuggestions.map((item) => (
                <tr key={item.id}>
                  <td>
                    <div className={styles.productNameCell}>
                      <span className={styles.productTitle}>{item.name}</span>
                      {item.barcode && (
                        <span className={styles.barcodeBadge}>
                          <Barcode size={12} /> {item.barcode}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    {item.notes ? (
                      <div className={styles.notesQuote}>"{item.notes}"</div>
                    ) : (
                      <span className={styles.metaText}>No customer notes provided</span>
                    )}
                  </td>
                  <td>
                    <span className={`${styles.statusPill} ${styles[`status${item.status}`]}`}>
                      {item.status === 'NEW' && <Sparkles size={13} />}
                      {item.status === 'ACKNOWLEDGED' && <Clock size={13} />}
                      {item.status === 'ADDED' && <CheckCircle2 size={13} />}
                      {item.status === 'REJECTED' && <XCircle size={13} />}
                      {item.status}
                    </span>
                  </td>
                  <td>
                    {item.opsNote ? (
                      <div className={styles.opsNoteWrap}>
                        <span className={styles.opsNoteText}>{item.opsNote}</span>
                        {item.updatedBy && (
                          <span className={styles.metaText}>By: {item.updatedBy}</span>
                        )}
                      </div>
                    ) : (
                      <span className={styles.metaText}>None</span>
                    )}
                  </td>
                  <td>
                    <div className={styles.productNameCell}>
                      <span className={styles.metaText}>{formatDate(item.createdAt)}</span>
                      {item.customerSub && (
                        <span className={styles.metaText} title={`User: ${item.customerSub}`}>
                          <User size={11} style={{ display: 'inline', marginRight: 3 }} />
                          {item.customerSub.slice(0, 12)}…
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className={styles.actionCell}>
                      <button
                        type="button"
                        className={styles.editBtn}
                        onClick={() => openUpdateDrawer(item)}
                      >
                        <Edit3 size={13} />
                        Update
                      </button>
                      <button
                        type="button"
                        className={styles.editBtn}
                        onClick={() => handleOnboardClick(item)}
                        title="Bulk onboard this product into store catalog"
                      >
                        <PlusCircle size={13} />
                        Onboard
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Drawer
        open={!!activeSuggestion}
        onClose={() => setActiveSuggestion(null)}
        title="Update Suggestion"
        subtitle={`Request ID: ${activeSuggestion?.id}`}
        width={440}
        footer={(
          <>
            <Button variant="ghost" onClick={() => setActiveSuggestion(null)} disabled={updating}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleUpdate} disabled={updating}>
              {updating ? 'Saving update…' : 'Save Status Update'}
            </Button>
          </>
        )}
      >
        {activeSuggestion && (
          <form onSubmit={handleUpdate}>
            <AuthMessage>{modalError}</AuthMessage>

            <div className={styles.suggestionSummaryCard}>
              <p className={styles.summaryTitle}>{activeSuggestion.name}</p>
              {activeSuggestion.barcode && (
                <p className={styles.summaryBarcode}>Barcode: {activeSuggestion.barcode}</p>
              )}
              {activeSuggestion.notes && (
                <p className={styles.summaryNotes}>"{activeSuggestion.notes}"</p>
              )}
            </div>

            <label className={form.field}>
              <span className={form.label}>Status</span>
              <select
                className={form.select}
                value={modalStatus}
                onChange={(e) => setModalStatus(e.target.value)}
              >
                <option value="NEW">NEW — Unreviewed</option>
                <option value="ACKNOWLEDGED">ACKNOWLEDGED — Reviewing with procurement</option>
                <option value="ADDED">ADDED — Added to store catalog</option>
                <option value="REJECTED">REJECTED — Declined</option>
              </select>
            </label>

            <label className={form.field}>
              <span className={form.label}>Ops Note / Status Comment</span>
              <textarea
                className={form.textarea}
                rows={3}
                value={opsNote}
                onChange={(e) => setOpsNote(e.target.value)}
                placeholder="e.g. We will review this with the procurement team"
              />
              <p className={form.hint}>
                This note provides context to store staff on procurement or catalog status.
              </p>
            </label>
          </form>
        )}
      </Drawer>
    </div>
  );
}
